import { createElement, useMemo, useState } from 'react';
import { LiksSearch } from '../LiksSearch/LiksSearch';
import {
  LUCIDE_ICON_NAMES,
  normalizeLucideIconName,
  resolveLucideIcon,
} from '../../../utils/lucideIconCatalog';
import './LiksIconPicker.css';

export type LiksIconPickerProps = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  searchPlaceholder: string;
  searchClearLabel: string;
  noResultsLabel: string;
  getOptionLabel: (iconName: string) => string;
};

export function LiksIconPicker({
  value,
  onChange,
  label,
  searchPlaceholder,
  searchClearLabel,
  noResultsLabel,
  getOptionLabel,
}: LiksIconPickerProps) {
  const [query, setQuery] = useState('');
  const normalizedValue = normalizeLucideIconName(value);
  const hasSelection = value.trim() !== '';

  const filteredIconNames = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    if (trimmedQuery === '') {
      return LUCIDE_ICON_NAMES;
    }

    return LUCIDE_ICON_NAMES.filter((iconName) =>
      iconName.includes(trimmedQuery),
    );
  }, [query]);

  return (
    <div className="liks-icon-picker">
      <span className="liks-icon-picker__label">{label}</span>
      <LiksSearch
        value={query}
        onChange={setQuery}
        placeholder={searchPlaceholder}
        clearLabel={searchClearLabel}
        variant="on-light"
        aria-label={searchPlaceholder}
        leadingIcon={
          hasSelection ? (
            <span className="liks-search__leading-icon" aria-hidden>
              {createElement(resolveLucideIcon(normalizedValue), {
                strokeWidth: 2,
              })}
            </span>
          ) : undefined
        }
      />
      <div className="liks-icon-picker__grid-wrap">
        {filteredIconNames.length === 0 ? (
          <p className="liks-icon-picker__empty">{noResultsLabel}</p>
        ) : (
          <ul
            className="liks-icon-picker__grid"
            role="listbox"
            aria-label={label}
          >
            {filteredIconNames.map((iconName) => {
              const isSelected = hasSelection && iconName === normalizedValue;

              return (
                <li key={iconName}>
                  <button
                    type="button"
                    className={[
                      'liks-icon-picker__option',
                      isSelected ? 'liks-icon-picker__option--selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="option"
                    aria-selected={isSelected}
                    aria-label={getOptionLabel(iconName)}
                    title={iconName}
                    onClick={() => onChange(iconName)}
                  >
                    {createElement(resolveLucideIcon(iconName), {
                      className: 'liks-icon-picker__option-icon',
                      strokeWidth: 2,
                      'aria-hidden': true,
                    })}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
